import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import api from "../lib/api";
import { toast } from "sonner";
import { Store, Truck, Mail, Phone } from "lucide-react";

const SHIPPING_FLAT = 15.0;

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    email: user?.email || "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "AU",
    payment_method: "pickup",
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const shippingFee = form.payment_method === "delivery" && items.length ? SHIPPING_FLAT : 0;
  const total = subtotal + shippingFee;

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    setSubmitting(true);
    try {
      const customer = {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        country: form.country,
        ...(form.payment_method === "delivery"
          ? {
              address_line1: form.address_line1,
              address_line2: form.address_line2,
              city: form.city,
              state: form.state,
              postal_code: form.postal_code,
            }
          : {
              address_line1: "",
              address_line2: "",
              city: "",
              state: "",
              postal_code: "",
            }),
      };
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer,
        payment_method: form.payment_method,
        origin_url: window.location.origin,
        user_email: user?.email || null,
      };
      const { data } = await api.post("/checkout/session", payload);
      setConfirmation({ ...data });
      clear();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not place order. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Order failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    const isDelivery = confirmation.type === "delivery";
    return (
      <div className="px-6 md:px-12 lg:px-24 py-20 max-w-3xl mx-auto" data-testid="order-confirmation">
        <p className="overline text-primary mb-4">Order reserved</p>
        <h1 className="editorial-heading text-4xl md:text-5xl text-secondary mb-6">
          We&rsquo;ll be in touch shortly.
        </h1>
        <p className="text-muted-foreground mb-10">
          Your order <strong className="text-secondary">#{confirmation.order_id.slice(0, 8)}</strong>{" "}
          has been reserved for{" "}
          <strong className="text-secondary">${confirmation.amount.toFixed(2)} AUD</strong>.
          {isDelivery
            ? " We'll contact you within one business day to confirm delivery details and arrange payment."
            : " We'll contact you within one business day to arrange a convenient pickup time and confirm the location. Payment is made when you collect your order."}
        </p>
        <div className="border border-border bg-card p-6 space-y-4">
          <div className="flex items-start gap-3">
            {isDelivery ? (
              <Truck className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            ) : (
              <Store className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            )}
            <div>
              <p className="overline text-muted-foreground mb-1">Fulfillment</p>
              <p className="text-sm text-secondary capitalize">
                {confirmation.type}
                {isDelivery ? "" : " — pay on pickup"}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 pt-4 border-t border-border">
            <Mail className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="overline text-muted-foreground mb-1">Next step</p>
              <p className="text-sm text-secondary">
                We&rsquo;ll email or call you on the contact details you provided.
              </p>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-8 leading-relaxed">
          A confirmation email has been sent to your address. Replies go directly to
          info@regenxwellness.com.au.
        </p>
        <div className="mt-10 flex gap-4">
          <Link
            to="/"
            data-testid="confirmation-back-home"
            className="inline-flex items-center justify-center bg-secondary text-secondary-foreground px-8 py-4 uppercase tracking-[0.22em] text-xs hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            Return home
          </Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="px-6 py-32 text-center" data-testid="checkout-empty">
        <h1 className="editorial-heading text-4xl text-secondary mb-4">Your cart is empty</h1>
        <Link
          to="/"
          className="text-xs uppercase tracking-[0.22em] text-primary border-b border-primary"
        >
          Browse the catalog
        </Link>
      </div>
    );
  }

  const isDelivery = form.payment_method === "delivery";

  return (
    <div data-testid="checkout-page" className="animate-in fade-in duration-500">
      <section className="px-6 md:px-12 lg:px-24 pt-16 pb-6">
        <p className="overline text-primary mb-4">Checkout</p>
        <h1 className="editorial-heading text-4xl md:text-5xl text-secondary">
          Reserve your order
        </h1>
        <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
          We&rsquo;ll be in touch within one business day to arrange pickup or delivery and confirm payment.
        </p>
      </section>

      <form onSubmit={submit} className="px-6 md:px-12 lg:px-24 pb-24 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-7 space-y-12">
          {/* Fulfillment */}
          <div>
            <h2 className="overline text-secondary mb-5">How would you like to receive it?</h2>
            <div
              data-testid="checkout-melbourne-notice"
              className="mb-4 border border-primary/30 bg-primary/5 px-4 py-3 text-xs text-secondary leading-relaxed"
            >
              <span className="overline text-primary mr-2">Please note</span>
              Delivery and pickup are currently available within <strong>Melbourne</strong> only.
              Australia-wide delivery is coming soon.
            </div>
            <RadioGroup
              value={form.payment_method}
              onValueChange={(v) => setField("payment_method", v)}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
            >
              <label
                htmlFor="ff-pickup"
                data-testid="fulfillment-pickup"
                className={`flex items-start gap-3 border p-5 cursor-pointer transition-colors ${
                  form.payment_method === "pickup" ? "border-secondary" : "border-border"
                }`}
              >
                <RadioGroupItem id="ff-pickup" value="pickup" />
                <div>
                  <p className="text-sm text-secondary font-medium flex items-center gap-2">
                    <Store className="h-4 w-4" /> Pickup
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Pay when you collect your order. We&rsquo;ll arrange a time with you.
                  </p>
                </div>
              </label>
              <label
                htmlFor="ff-delivery"
                data-testid="fulfillment-delivery"
                className={`flex items-start gap-3 border p-5 cursor-pointer transition-colors ${
                  form.payment_method === "delivery" ? "border-secondary" : "border-border"
                }`}
              >
                <RadioGroupItem id="ff-delivery" value="delivery" />
                <div>
                  <p className="text-sm text-secondary font-medium flex items-center gap-2">
                    <Truck className="h-4 w-4" /> Delivery
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Melbourne metro only. We&rsquo;ll confirm details and payment after you order.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Contact */}
          <div>
            <h2 className="overline text-secondary mb-5">Contact</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name">Full name</Label>
                <Input id="full_name" required value={form.full_name} onChange={(e) => setField("full_name", e.target.value)} data-testid="checkout-name" className="rounded-none mt-1.5" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={form.email} onChange={(e) => setField("email", e.target.value)} data-testid="checkout-email" className="rounded-none mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="phone">Phone (recommended)</Label>
                <Input id="phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="checkout-phone" className="rounded-none mt-1.5" />
              </div>
            </div>
          </div>

          {/* Delivery address (conditional) */}
          {isDelivery && (
            <div data-testid="delivery-address-section">
              <h2 className="overline text-secondary mb-5">Delivery address</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="address_line1">Address line 1</Label>
                  <Input id="address_line1" required value={form.address_line1} onChange={(e) => setField("address_line1", e.target.value)} data-testid="checkout-address1" className="rounded-none mt-1.5" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="address_line2">Address line 2 (optional)</Label>
                  <Input id="address_line2" value={form.address_line2} onChange={(e) => setField("address_line2", e.target.value)} data-testid="checkout-address2" className="rounded-none mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input id="city" required value={form.city} onChange={(e) => setField("city", e.target.value)} data-testid="checkout-city" className="rounded-none mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input id="state" required value={form.state} onChange={(e) => setField("state", e.target.value)} data-testid="checkout-state" className="rounded-none mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="postal_code">Postcode</Label>
                  <Input id="postal_code" required value={form.postal_code} onChange={(e) => setField("postal_code", e.target.value)} data-testid="checkout-postal" className="rounded-none mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input id="country" required value={form.country} onChange={(e) => setField("country", e.target.value)} data-testid="checkout-country" className="rounded-none mt-1.5" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <aside className="md:col-span-5 md:sticky md:top-32 self-start bg-muted p-8 border border-border">
          <h2 className="editorial-heading text-2xl text-secondary mb-6">Order summary</h2>
          <ul className="divide-y divide-border mb-6" data-testid="checkout-items">
            {items.map((it) => (
              <li key={it.product_id} className="flex gap-4 py-4">
                <img src={it.image} alt={it.name} className="w-16 h-20 object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-secondary truncate">{it.name}</p>
                  <p className="text-xs text-muted-foreground">Qty {it.quantity}</p>
                </div>
                <p className="text-sm text-secondary">${(it.price * it.quantity).toFixed(2)}</p>
              </li>
            ))}
          </ul>
          <div className="space-y-2 text-sm pb-6 border-b border-border">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="text-secondary">${subtotal.toFixed(2)}</span>
            </div>
            {isDelivery ? (
              <div className="flex justify-between" data-testid="checkout-shipping-row">
                <span className="text-muted-foreground">Shipping</span>
                <span className="text-secondary">${SHIPPING_FLAT.toFixed(2)}</span>
              </div>
            ) : (
              <div className="flex justify-between" data-testid="checkout-pickup-row">
                <span className="text-muted-foreground">Pickup</span>
                <span className="text-secondary">No shipping fee</span>
              </div>
            )}
          </div>
          <div className="flex justify-between text-base pt-5 mb-8">
            <span className="text-secondary font-medium">Total</span>
            <span className="text-secondary font-medium" data-testid="checkout-total">
              ${total.toFixed(2)} AUD
            </span>
          </div>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="checkout-submit"
            className="w-full rounded-none bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground uppercase tracking-[0.22em] text-xs py-6"
          >
            {submitting ? "Reserving…" : "Reserve order"}
          </Button>
          <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground text-center mt-5 flex items-center justify-center gap-2">
            <Phone className="h-3 w-3" /> We&rsquo;ll be in touch within 1 business day
          </p>
        </aside>
      </form>
    </div>
  );
}
